import { api } from './client';
import { ApiError } from './types';

/**
 * In-app voice calling, as the platform exposes it to a customer.
 *
 * ---------------------------------------------------------------------------
 * WHY THE APP NEVER TALKS TO THE VOICE VENDOR
 * ---------------------------------------------------------------------------
 * The vendor's API key authenticates a *backend*, and their integration guide
 * is blunt about it: a key that ships in an APK is a public key. So nothing in
 * this file names the vendor, and the only credential that ever reaches this
 * handset is the per-call `token` below - scoped to one call, valid for
 * minutes, and safe to hold.
 *
 * Every route here is our own backend under `/customers/me/voice`, which
 * proxies to the vendor and adds the thing the vendor cannot know: whether
 * this customer is allowed to ring this professional right now.
 *
 * ---------------------------------------------------------------------------
 * THE SERVER OWNS THE CALL, NOT THE APP
 * ---------------------------------------------------------------------------
 * A call becomes `ACCEPTED` because the callee hit the accept endpoint, not
 * because a socket opened; it becomes `MISSED` because the server's ring timer
 * ran out, not because this app counted to 45. Anything on screen that claims
 * a state must have read it back from one of these calls. That single rule is
 * why the provider polls rather than guesses.
 */

/** What a call hangs off. A ride is standalone; a booking is a job. */
export type VoiceSubjectType = 'booking' | 'ride';

export type VoiceCallStatus =
  | 'RINGING'
  | 'ACCEPTED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'REJECTED'
  | 'MISSED'
  | 'FAILED';

/**
 * Who is on the other end.
 *
 * A display name and a role, and deliberately nothing else. There is no phone
 * number in this type because there is no phone number in the product - both
 * sides connect over data, and a number appearing anywhere in the UI or the
 * logs would mean something has gone wrong upstream.
 */
export type VoiceCounterpart = {
  displayName: string;
  role: 'PRO' | 'CUSTOMER' | 'RIDER';
};

/**
 * Why the call button is not offered, when it is not.
 *
 * Distinct reasons rather than one boolean, because they read differently to
 * the customer: nobody has been assigned yet is a "not yet", being on another
 * call is a "not now", and a deployment with no voice configured is a "not at
 * all". `src/voice/types.ts` turns each into a sentence.
 */
export type VoiceEligibilityReason =
  | 'NOT_YET_ASSIGNED'
  | 'SUBJECT_NOT_LIVE'
  | 'COUNTERPART_UNAVAILABLE'
  | 'YOU_ARE_ON_A_CALL'
  | 'VOICE_NOT_CONFIGURED';

export type VoiceEligibility = {
  canCall: boolean;
  reason: VoiceEligibilityReason | null;
  /** Null whenever there is nobody to call - an unassigned booking, say. */
  counterpart: VoiceCounterpart | null;
  /**
   * The live call behind `YOU_ARE_ON_A_CALL`, set only alongside that reason.
   * Lets the button end a call this handset lost track of - a kill mid-call,
   * say - instead of waiting on the server's own multi-hour sweep.
   */
  callId: string | null;
};

/** One call, in whatever state the server last put it. */
export type VoiceCall = {
  callId: string;
  status: VoiceCallStatus;
  subjectType: VoiceSubjectType;
  subjectId: string;
  /** Which end of it this customer is on. */
  role: 'caller' | 'callee';
  counterpart: VoiceCounterpart;
  /** The server's ring timer, in seconds. The app runs no timer of its own. */
  ringTimeoutSeconds: number;
  ringingAt: string | null;
  acceptedAt: string | null;
  endedAt: string | null;
  /** Talk time, measured from acceptance. Null until the call completes. */
  durationSeconds: number | null;
  failureReason: string | null;
};

/**
 * What placing a call answers with.
 *
 * `ALREADY_RINGING` is not a failure. It means a call on this subject is
 * already up, which happens two ways: a double tap, and glare - the other side
 * dialled at the same moment and won the race. `role` is what separates them.
 * As `caller` this is our own call coming back; as `callee` the customer is
 * being rung right now and the UI has to flip from dialling to answering.
 *
 * `token` is null for a callee, because a callee has not accepted yet. It is
 * fetched from `/accept`, which is also what actually marks the call answered.
 */
export type VoiceCallStart = {
  status: 'RINGING' | 'ALREADY_RINGING';
  callId: string;
  role: 'caller' | 'callee';
  token: string | null;
  voiceServerUrl: string | null;
  ringTimeoutSeconds: number;
  counterpart: VoiceCounterpart;
};

/** A credential for the audio engine, and where to point it. */
export type VoiceGrant = {
  token: string;
  voiceServerUrl: string;
};

const BASE = '/customers/me/voice';

// ------------------------------------------------------------------ the calls

/**
 * Whether this customer may ring the other party on this subject, right now.
 *
 * Asked before the button is drawn rather than when it is pressed: an offer
 * that fails on tap is worse than one that was never made, and the reasons a
 * call is refused (nobody assigned yet, the job is over) are all things the
 * customer can see for themselves on the same screen.
 */
export const fetchVoiceEligibility = (
  subjectType: VoiceSubjectType,
  subjectId: string,
  signal?: AbortSignal,
) =>
  api.get<VoiceEligibility>(`${BASE}/eligibility`, {
    query: { subjectType, subjectId },
    signal,
  });

export const startVoiceCall = (
  subjectType: VoiceSubjectType,
  subjectId: string,
) => api.post<VoiceCallStart>(`${BASE}/calls`, { subjectType, subjectId });

/**
 * The Call button's number to dial.
 *
 * The server remembers which booking this tap was for, for two minutes, so a
 * call arriving on the customer's SIM - which carries no booking - still rings
 * that booking's Pro rather than whichever job happens to be live.
 */
export const prepareVoiceDial = (
  subjectType: VoiceSubjectType,
  subjectId: string,
) =>
  api.post<{ phone: string }>(`${BASE}/dial`, { subjectType, subjectId });

export const fetchVoiceCall = (callId: string, signal?: AbortSignal) =>
  api.get<VoiceCall>(`${BASE}/calls/${callId}`, { signal });

/**
 * The call ringing this handset, if there is one.
 *
 * The reliability net beside push. A notification can be dropped by the OS, by
 * a battery optimiser, or by a token the server has not caught up with, and
 * none of those tell anybody. This is polled while the app is open and idle so
 * a call still reaches someone who is holding the phone.
 */
export const fetchIncomingVoiceCall = (signal?: AbortSignal) =>
  api.get<VoiceCall | null>(`${BASE}/calls/incoming`, { signal });

/** Answers. This is what makes the call `ACCEPTED` - connecting does not. */
export const acceptVoiceCall = (callId: string) =>
  api.post<VoiceGrant>(`${BASE}/calls/${callId}/accept`);

export const rejectVoiceCall = (callId: string) =>
  api.post<VoiceCall>(`${BASE}/calls/${callId}/reject`);

/**
 * Hangs up, from either end and at any stage.
 *
 * Idempotent by design - both apps racing to report the same hang-up is the
 * ordinary case, not an error - so nothing above this should ever show a
 * failure from a second `end`.
 */
export const endVoiceCall = (callId: string) =>
  api.post<VoiceCall>(`${BASE}/calls/${callId}/end`);

/**
 * A fresh credential for a call that is already up.
 *
 * For reconnects. The per-call token is short-lived, so a handset that drops
 * off the network for a minute and comes back cannot reuse the one it joined
 * with; it asks for another and rejoins the same room.
 */
export const refreshVoiceToken = (callId: string) =>
  api.post<VoiceGrant>(`${BASE}/calls/${callId}/token`);

// ------------------------------------------------------------------ failures

/**
 * The failures worth branching on.
 *
 * Everything else that goes wrong is already a sentence a customer can read -
 * `client.ts` sees to that. These are different because the app has to *do*
 * something specific about each: stop ringing, refuse to dial, close the
 * overlay, or say something other than what the generic message would say.
 */
export type VoiceErrorCode =
  | 'CALL_NO_LONGER_RINGING'
  | 'YOU_ARE_ON_A_CALL'
  | 'VOICE_NOT_CONFIGURED'
  | 'SESSION_NOT_ACTIVE'
  /**
   * Their handset could not be rung at all - no push token on file, or a dead
   * one. The server ends the call immediately rather than letting the caller
   * listen to forty-five seconds of ringback for a phone that was never
   * contacted, so this must NOT be worded as "no answer": nobody ignored them.
   */
  | 'CALLEE_UNREACHABLE'
  /**
   * Not on this booking, or not the callee on this call. Should not happen in
   * normal use - it means the screen is acting on something stale.
   */
  | 'NOT_A_PARTICIPANT';

const VOICE_ERROR_CODES: VoiceErrorCode[] = [
  'CALL_NO_LONGER_RINGING',
  'YOU_ARE_ON_A_CALL',
  'VOICE_NOT_CONFIGURED',
  'SESSION_NOT_ACTIVE',
  'CALLEE_UNREACHABLE',
  'NOT_A_PARTICIPANT',
];

/**
 * Pulls the machine-readable code out of a failure, when it carries one.
 *
 * The server puts it in `errors[].code` beside the human sentence, so this
 * reads the details rather than matching on the message - a message is written
 * for a person and is allowed to change wording without warning.
 */
export const voiceErrorCode = (thrown: unknown): VoiceErrorCode | null => {
  if (!(thrown instanceof ApiError)) {
    return null;
  }
  for (const detail of thrown.errors) {
    const code = detail.code as VoiceErrorCode | undefined;
    if (code && VOICE_ERROR_CODES.includes(code)) {
      return code;
    }
  }
  return null;
};
